import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Box,
  Chip,
  Stack,
} from '@mui/material';
import { CustomRadioButton as RadioButton } from './RadioButton';
import { RadioButtonGroupProps, GridLayoutProps } from './RadioButton.props';

const spacingMap = {
  s: 1,
  m: 2,
  l: 3,
  xl: 4,
} as const;

// Map RadioButton colors to MUI Chip colors
const getChipColor = (
  color: string
):
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning'
  | 'default' => {
  switch (color) {
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'secondary';
    case 'success':
      return 'success';
    case 'error':
    case 'destructive':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'primary';
  }
};

/**
 * Calculate grid container styles based on grid configuration
 */
const getGridContainerStyles = (gridProps?: GridLayoutProps) => {
  if (!gridProps) return {};

  const { columns = 3, gap = '16px', maxWidth } = gridProps;
  const gapValue = typeof gap === 'number' ? `${gap}px` : gap;

  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: gapValue,
    width: '100%',
    maxWidth: maxWidth || '100%',
  };
};

export const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  options,
  value,
  onChange,
  name = 'radio-group',
  title,
  titleProps = { spacing: 'm' },
  optionsProps = { spacing: 'm', layout: 'column' },
  size = 'm',
  color = 'primary',
  variant = 'filled',
  disabled = false,
  displayMode = 'default',
  sx,
  ...radioGroupProps
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
  };

  const handleChipClick = (optionValue: string) => {
    if (!disabled) {
      // Create a synthetic event for chip mode
      const syntheticEvent = {
        target: { value: optionValue },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);
    }
  };

  if (displayMode === 'default') {
    return (
      <Box sx={sx}>
        <FormControl component="fieldset" disabled={disabled}>
          {title && (
            <FormLabel
              component="legend"
              sx={{
                mb: spacingMap[titleProps.spacing || 'm'],
                color: 'text.primary',
                '&.Mui-focused': {
                  color: 'text.primary',
                },
              }}
            >
              {title}
            </FormLabel>
          )}
          <RadioGroup
            {...radioGroupProps}
            name={name}
            value={value || ''}
            onChange={handleChange}
            row={optionsProps.layout === 'row'}
            sx={{
              gap: spacingMap[optionsProps.spacing || 'm'],
            }}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                disabled={option.disabled || disabled}
                control={<RadioButton size={size} color={color} />}
                label={
                  <Box>
                    <Box component="span">{option.label}</Box>
                    {option.description && (
                      <Box
                        component="div"
                        sx={{
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                          mt: 0.25,
                        }}
                      >
                        {option.description}
                      </Box>
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
    );
  }

  // Chip mode
  const isGridLayout = optionsProps.layout === 'grid';
  const gridProps = optionsProps.gridProps;

  return (
    <Box sx={sx}>
      {title && (
        <Box
          component="div"
          sx={{
            mb: spacingMap[titleProps.spacing || 'm'],
            ...titleProps.sx,
          }}
        >
          {title}
        </Box>
      )}

      {isGridLayout ? (
        // Grid Layout Mode - Uses CSS Grid for consistent sizing
        <Box sx={getGridContainerStyles(gridProps)}>
          {options.map((option) => {
            const isSelected = value === option.value;
            const isDisabled = option.disabled || disabled;

            return (
              <Chip
                key={option.value}
                label={option.label}
                variant={isSelected ? variant : 'outlined'}
                disabled={isDisabled}
                onClick={() => handleChipClick(option.value)}
                color={isSelected ? getChipColor(color) : 'default'}
                sx={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              />
            );
          })}
        </Box>
      ) : (
        // Stack Layout Mode (row/column)
        <Stack
          direction={optionsProps.layout === 'row' ? 'row' : 'column'}
          spacing={spacingMap[optionsProps.spacing || 'm']}
          flexWrap="wrap"
        >
          {options.map((option) => {
            const isSelected = value === option.value;
            const isDisabled = option.disabled || disabled;

            return (
              <Chip
                key={option.value}
                label={option.label}
                variant={isSelected ? variant : 'outlined'}
                disabled={isDisabled}
                onClick={() => handleChipClick(option.value)}
                color={isSelected ? getChipColor(color) : 'default'}
                sx={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
